import IconInfo from '@apollo/icons/default/IconInfo.svg';
import { Link, Select } from '@apollo/orbit';
import { Button } from '@apollo/space-kit/Button';
import { colors } from '@apollo/space-kit/colors';
import classnames from 'classnames';
import React, { useContext, useEffect, useState } from 'react';

import * as orgRoutes from 'src/app/account/routes';
import { Icon as IconAws } from 'src/assets/icon-aws-logo.svg';
import { Icon as IconAzure } from 'src/assets/icon-azure-logo.svg';
import { Icon as IconGcp } from 'src/assets/icon-gcp-logo.svg';
import { Loading } from 'src/components/common/loading/Loading';
import { Tooltip } from 'src/components/common/tooltip/Tooltip';
import { ErrorMessage } from 'src/components/errorMessage/ErrorMessage';
import { useCloudRegionsQuery } from 'src/hooks/useCloudOnboarding';
import { useRouteParams } from 'src/hooks/useRouteParams';
import { CloudProvider, RegionState } from 'src/lib/graphqlTypes/types';

import { FlowTypeContext } from '../FlowTypeProvider';
import {
  ButtonState,
  NewSupergraphOnboardingControls,
} from '../NewSupergraphOnboardingControls';
import { NewSupergraphOnboardingStepLayout } from '../NewSupergraphOnboardingStepLayout';
import {
  CommonCloudDedicatedProps,
  CommonStepProps,
  SupergraphOnboardingStepPrivate,
  SupergraphOnboardingStepPublic,
} from '../shared';

import { AvailableSubgraphUrls } from './availableSubgraphUrls/AvailableSubgraphUrls';
import { VariantOnboardingFields } from './VariantOnboardingFields';

export const SelectDedicatedPlanStep = ({
  setPreviousStep,
  previousStep,
  values,
  ...contentProps
}: Pick<CommonStepProps, 'values'> &
  React.ComponentProps<typeof SelectDedicatedPlanStepContent> &
  Omit<
    React.ComponentProps<typeof NewSupergraphOnboardingStepLayout>,
    'description' | 'image' | 'children' | 'stepId'
  >) => {
  const { flowType } = useContext(FlowTypeContext);
  return (
    <NewSupergraphOnboardingStepLayout
      values={values}
      stepId={
        flowType === 'DEDICATED_PUBLIC'
          ? SupergraphOnboardingStepPublic.DedicatedProvider
          : SupergraphOnboardingStepPrivate.YourPlan
      }
      previousStep={previousStep}
      setPreviousStep={setPreviousStep}
      className="w-120"
      hasPrivateSubgraphs={contentProps.hasPrivateSubgraphs}
    >
      <SelectDedicatedPlanStepContent {...contentProps} values={values} />
    </NewSupergraphOnboardingStepLayout>
  );
};

const SelectDedicatedPlanStepContent = ({
  errors,
  handleBlur,
  touched,
  values,
  setValues,
  setFieldValue,
  setFieldTouched,
  continueAnalyticsLabelsByStep,
  whenStepsBegan,
  nextButtonState,
  setNextButtonState,
  creatingSupergraph,
  availableUnregisteredResources,
  isDedicatedPlan,
  existingGraphVariants,
  selectedPrivateDomainName,
  setSelectedPrivateDomainName,
}: CommonStepProps &
  CommonCloudDedicatedProps & {
    nextButtonState: ButtonState;
    setNextButtonState: React.Dispatch<React.SetStateAction<ButtonState>>;
  }) => {
  const { flowType } = useContext(FlowTypeContext);
  const { includeVariantOnboardingStep } = useRouteParams(
    orgRoutes.supergraphOnboarding,
  );
  const isVariantOnboardingFlow = includeVariantOnboardingStep === 'true';
  const { data: cloudRegionsData, loading: cloudRegionsLoading } =
    useCloudRegionsQuery();

  let activeRegions = cloudRegionsData?.cloud?.regions.filter((r) => {
    return r.state === 'ACTIVE';
  });

  if (activeRegions) {
    const newRegion = {
      name: 'Region not listed',
      code: 'RNL',
      provider: CloudProvider.AWS,
      state: RegionState.INACTIVE,
      country: 'Region not listed',
    };

    activeRegions = [
      ...activeRegions,
      {
        __typename: 'RegionDescription',
        ...newRegion,
      },
    ];
  }

  const [showAvailbleSubgraphsModal, setShowAvailableSubgraphsModal] =
    useState(false);

  useEffect(() => {
    if (availableUnregisteredResources.length) {
      setShowAvailableSubgraphsModal(true);
    }
  }, [availableUnregisteredResources.length]);

  useEffect(() => {
    // we always programatically fill these fields,
    // so we don't want to check if they have been touched before disabling

    const shouldDisableNext =
      values.cloudRegion === '' ||
      values.cloudRegion === null ||
      (isVariantOnboardingFlow &&
        (!!errors.variantName || !values.variantName));

    setNextButtonState({
      isDisabled: !!shouldDisableNext,
      disabledReason: 'cloud region not defined',
    });
  }, [
    errors.variantName,
    setNextButtonState,
    values.cloudRegion,
    values.variantName,
    isVariantOnboardingFlow,
  ]);

  const handleOnChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    if (e.target.value === 'RNL') {
      window.open(
        'https://docs.google.com/forms/d/1FWhOJpDV-mociOv_-SihpCBjL6Yx4dk9dBe3AiPKjVk/edit',
        '_blank',
      );

      setFieldValue(
        'cloudRegion',
        activeRegions ? activeRegions[0]?.code : 'us-east-1',
      );

      return;
    }
    setFieldValue('cloudRegion', e.target.value);
  };

  const [selectedProvider, setSelectedProvider] = useState<
    'AWS' | 'GCP' | 'AZURE'
  >('AWS');

  if (cloudRegionsLoading) {
    return (
      <div className="flex h-full items-center lg:my-44 lg:h-auto">
        <Loading />
      </div>
    );
  }

  if (!cloudRegionsData?.cloud?.regions) {
    return (
      <div className="flex h-full items-center">
        <ErrorMessage />
      </div>
    );
  }

  return (
    <div>
      {isVariantOnboardingFlow && (
        <VariantOnboardingFields
          values={values}
          errors={errors}
          handleBlur={handleBlur}
          touched={touched}
          setFieldTouched={setFieldTouched}
          setValues={setValues}
          setFieldValue={setFieldValue}
          existingGraphVariants={existingGraphVariants}
        />
      )}
      <div
        className={classnames(
          'mb-2 font-semibold',
          isVariantOnboardingFlow ? 'text-lg' : 'text-2xl',
        )}
      >
        Choose your backend provider
      </div>
      <div className="mb-6 flex text-sm text-secondary">
        We will deploy your GraphOS service to the provider you select below.
        Your service will be hosted in the region you select.
      </div>
      <div className="mb-2 text-lg font-semibold">Providers</div>
      <div className="mb-6 flex">
        <Button
          className={classnames(
            'mr-3 box-content flex h-10 w-32 items-center justify-center p-4',
            selectedProvider === 'AWS' && 'border-blue-light bg-blue-lightest',
          )}
          color={
            selectedProvider === 'AWS' ? colors.silver.lighter : colors.white
          }
          onClick={() => {
            setFieldValue('cloudProvider', CloudProvider.AWS);
            setSelectedProvider('AWS');
          }}
        >
          <IconAws className="size-full" />
        </Button>

        <Tooltip
          label={tooltipContent('Google Cloud Platform')}
          interactive
          placement="auto-end"
        >
          <Button
            className="mr-3 box-content flex h-10 w-32 cursor-not-allowed items-center justify-center p-4"
            color={
              selectedProvider === 'GCP' ? colors.silver.lighter : colors.white
            }
            onClick={() => {
              setSelectedProvider('GCP');
            }}
          >
            <IconGcp className="size-full" />
          </Button>
        </Tooltip>
        <Tooltip
          label={tooltipContent('Azure')}
          interactive
          placement="auto-end"
        >
          <Button
            className="mr-3 box-content flex h-10 w-32 cursor-not-allowed items-center justify-center p-4"
            color={
              selectedProvider === 'AZURE'
                ? colors.silver.lighter
                : colors.white
            }
            onClick={() => {
              setSelectedProvider('AZURE');
            }}
          >
            <IconAzure className="size-full" />
          </Button>
        </Tooltip>
      </div>

      <div className="mb-2 text-lg font-semibold">
        Regions{' '}
        <Tooltip
          className="w-72"
          label="This will be the region where your GraphOS Router is hosted.
          It is best to choose a region that is close to your hosted services."
        >
          <IconInfo className="text-secondary" />
        </Tooltip>
      </div>
      <div className="mb-20 flex flex-row">
        <Select
          className="mr-4"
          id="cloudProviderRegion"
          name="cloudProviderRegion"
          onChange={handleOnChange}
          value={values.cloudRegion ?? activeRegions?.[0]?.code ?? 'us-east-1'}
        >
          {activeRegions?.map((region) => (
            <option key={region.code} value={region.code}>
              {region.name} - {region.code}
            </option>
          ))}
        </Select>
      </div>
      <div className="hidden items-center justify-between px-0 py-16 3xl:flex">
        <NewSupergraphOnboardingControls.CancelButton
          creatingSupergraph={creatingSupergraph}
        />

        <NewSupergraphOnboardingControls.BackButton
          creatingSupergraph={creatingSupergraph}
          className="ml-auto"
        />
        <NewSupergraphOnboardingControls.NextButton
          whenStepsBegan={whenStepsBegan}
          smallScreen={false}
          creatingSupergraph={creatingSupergraph}
          loading={cloudRegionsLoading}
          state={nextButtonState}
          continueAnalyticsLabelsByStep={continueAnalyticsLabelsByStep}
        />
      </div>
      <div>
        {availableUnregisteredResources.length > 0 &&
          !isVariantOnboardingFlow && (
            <AvailableSubgraphUrls
              isOpen={showAvailbleSubgraphsModal}
              onClose={() => setShowAvailableSubgraphsModal(false)}
              availableUnregisteredResources={availableUnregisteredResources}
              values={values}
              setFieldValue={setFieldValue}
              flowType={flowType}
              isDedicatedPlan={isDedicatedPlan}
              isVariantOnboardingFlow={isVariantOnboardingFlow}
              selectedPrivateDomainName={selectedPrivateDomainName}
              setSelectedPrivateDomainName={setSelectedPrivateDomainName}
            />
          )}
      </div>
    </div>
  );
};

const tooltipContent = (provider: string) => (
  <span>
    {provider} is currently not available for Cloud Router.{' '}
    <Link
      to="https://docs.google.com/forms/d/1FWhOJpDV-mociOv_-SihpCBjL6Yx4dk9dBe3AiPKjVk/edit"
      isExternal
    >
      Contact us
    </Link>{' '}
    to let us know which provider you would like us to provide access to.
  </span>
);
